import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { convertDocxToHtml } from '../../lib/docx';
import {
    stripDocxExtension,
    type ParsedPolicy,
} from '../department-policies/upload/DocxPolicyUpload';
import PolicyEditDialog from '../department-policies/upload/PolicyEditDialog';
import { savePolicyDocument } from '../../services/api/policyService';
import FacilityPolicyList from './FacilityPolicyList';

interface FacilityPoliciesSectionProps {
    policies: ParsedPolicy[];
    onAdd: (policy: ParsedPolicy) => Promise<void>;
    onRemove: (policy: ParsedPolicy) => Promise<void>;
    onUpdate?: (updated: ParsedPolicy) => void;
}

export default function FacilityPoliciesSection({
    policies,
    onAdd,
    onRemove,
    onUpdate,
}: FacilityPoliciesSectionProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dialogPolicy, setDialogPolicy] = useState<ParsedPolicy | null>(null);

    const handleFileChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;

            if (policies.some((p) => p.fileName && p.fileName === file.name)) {
                toast({
                    title: t('facilities.policiesSection.alreadyUploaded'),
                    description: t('facilities.policiesSection.alreadyUploadedDesc', { fileName: file.name }),
                    variant: 'destructive',
                });
                return;
            }

            try {
                const arrayBuffer = await file.arrayBuffer();
                const { html, warnings } = await convertDocxToHtml(arrayBuffer);
                setDialogPolicy({
                    fileName: file.name,
                    title: stripDocxExtension(file.name),
                    effectiveDate: '',
                    expiryDate: '',
                    tags: [],
                    html,
                    warnings,
                    parsedAt: Date.now(),
                });
            } catch (err) {
                toast({
                    title: t('facilities.policiesSection.uploadFailed'),
                    description:
                        err instanceof Error
                            ? err.message
                            : t('facilities.policiesSection.uploadFailedDesc', { fileName: file.name }),
                    variant: 'destructive',
                });
            }
        },
        [policies, toast, t]
    );

    const handleDialogSave = async (policy: ParsedPolicy): Promise<void> => {
        if (policy._id) {
            await savePolicyDocument(
                policy._id,
                { policyData: policy.html },
                policy.title,
                'Edited from onboarding',
                {
                    effectiveDate: new Date(policy.effectiveDate).toISOString(),
                    expiryDate: new Date(policy.expiryDate).toISOString(),
                    tags: policy.tags,
                }
            );
            onUpdate?.(policy);
        } else {
            await onAdd(policy);
        }
    };

    return (
        <section className="mt-10">
            <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2">
                        <h3 className="text-xl font-semibold text-foreground">
                            {t('facilities.policiesSection.title')}
                        </h3>
                        <span className="rounded-full bg-zinc-300 px-2 py-0.5 text-xs font-medium tabular-nums text-zinc-800">
                            {policies.length}
                        </span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {t('facilities.policiesSection.description')}
                    </p>
                </div>
                <Button size="sm" onClick={() => fileInputRef.current?.click()}>
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    {t('facilities.policiesSection.addPolicy')}
                </Button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".docx"
                    className="hidden"
                    onChange={handleFileChange}
                    aria-label="Upload policy document"
                />
            </div>

            {policies.length === 0 ? (
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-subtle bg-muted/30 p-10 transition-colors hover:border-emerald-500/40 hover:bg-muted/50"
                >
                    <FileText
                        className="h-8 w-8 text-muted-foreground"
                        aria-hidden="true"
                    />
                    <div className="text-center">
                        <p className="text-sm font-medium text-foreground">
                            {t('facilities.policiesSection.emptyTitle')}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                            {t('facilities.policiesSection.emptyDescription')}
                        </p>
                    </div>
                </button>
            ) : (
                <FacilityPolicyList
                    policies={policies}
                    onRemove={onRemove}
                    onPreview={setDialogPolicy}
                />
            )}

            <PolicyEditDialog
                open={dialogPolicy !== null}
                onOpenChange={(open) => {
                    if (!open) setDialogPolicy(null);
                }}
                policy={dialogPolicy}
                onSave={handleDialogSave}
            />
        </section>
    );
}
